/* eslint-disable no-underscore-dangle */
import { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import { subscriptionService, taskService } from "@miplatform/api";
import {
  ATTEMPTS_HUB_NAME,
  ATTEMPT_STATUSES,
  DATE_TIME_FORMATS,
  ENTITY_TYPES,
  HTTP_STATUSES,
  SELECTED_BOARD_KEY,
  SNAP_INTERVAL_IN_HOURS,
  VIEWS,
} from "@miplatform/constants";
import {
  CONNECTED as HUB_CONNECTED,
  createHub,
  findHubByName,
  removeHubByName,
  subscribeToAttemptUpdate,
  subscribeToHubStateChange,
  subscribeToUserUpdate,
} from "@miplatform/signalr";
import {
  appStorage,
  bugsnag,
  createUUID,
  deepCopy,
  formatDate,
  getDate,
  getDateWithAddedTime,
  getDurationInHours,
  isSameDay,
  isSameOrAfter,
  isSameOrBefore,
  isToday,
  snapAdjusted,
  sortOnField,
  toHour,
  toSnappedTime,
} from "@miplatform/utils";
import { FOLLOW_UP_TRAY_FILTERS } from "../../Config/Calendars/ConfigCalendarSetList.constants";
import { attachEventTime, getTimeSlot } from "../utils";
import { buildUserCapacitiesData } from "../utils/capacityUtil";

const DEFAULT_CAROUSEL_INTERVAL = 5;
/**
 * A pure React replacement for the old MST SchedulerViewModel.
 * All state is managed with useState/useRef and all actions are plain functions.
 */
export const useSchedulerViewModel = ({ viewConfig, calendarSet }) => {
  // ---------------- State ----------------
  const [users, setUsers] = useState([]); // UserModel[]
  const [boards, setBoards] = useState([]);
  const [boardUsers, setBoardUsers] = useState([]);
  const [selectedBoard, setSelectedBoardState] = useState({});
  const [data, setData] = useState([]); // attempts/events (augmented)
  const [buckets, setBuckets] = useState([]);

  const [carouselMode, setCarouselMode] = useState(false);
  const [carouselInterval, setCarouselIntervalState] = useState(
    DEFAULT_CAROUSEL_INTERVAL
  );
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const [userSignalRUpdated, setUserSignalRUpdated] = useState(0);

  const [isSignalRConnected, setIsSignalRConnected] = useState(false);
  const [calendarView, setCalendarView] = useState(VIEWS.DAY);

  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [date, setDateState] = useState(moment());
  const [dateRange, setDateRangeState] = useState({
    startDate: moment(),
    endDate: moment(getDateWithAddedTime(moment(), { d: 6 })),
  });
  const [closeoutDrawersMap, setCloseoutDrawersMap] = useState({});
  const [userCapacities, setUserCapacities] = useState({});

  // ---------------- Refs (mutable, not causing re-renders) ----------------
  const carouselTimerRef = useRef(null);
  const pauseCarouselTypesRef = useRef(new Set());
  const hubRef = useRef(null);
  const unsubscribeRef = useRef(null);
  const userSignalEventRef = useRef(null);

  // ---------------- Action: simple setters that normalized inputs ----------------
  const setDate = useCallback((value) => {
    setDateState(value);
  }, []);

  const setDateRange = useCallback((range) => {
    setDateRangeState({
      startDate: range.startDate,
      endDate: range.endDate,
    });
  }, []);

  const setCarouselInterval = useCallback((interval = null) => {
    setCarouselIntervalState(interval);
    setCarouselTimer(interval);
  }, []);

  // ---------------- Conflict checking ----------------

  const checkConflicts = useCallback((column = [], event = {}) => {
    if (column.length <= 1) return false;

    const isSameTimeSlot = (slotA, slotB) =>
      slotA[0] === slotB[0] && slotA[1] === slotB[1];

    const timeSlots = column
      .filter(
        ({ attempt }) =>
          ![ATTEMPT_STATUSES.SUBMITTED, ATTEMPT_STATUSES.CLOSED].includes(
            attempt?.attemptStatus
          )
      )
      .map(getTimeSlot)
      .sort(([a], [b]) => a - b);

    const eventTimeSlot = getTimeSlot(event);

    for (let i = 1; i < timeSlots.length; i++) {
      if (
        timeSlots[i][0] < timeSlots[i - 1][1] &&
        (isSameTimeSlot(timeSlots[i], eventTimeSlot) ||
          isSameTimeSlot(timeSlots[i - 1], eventTimeSlot))
      )
        return true;
    }
    return false;
  }, []);

  // ---------------- Display data computation ----------------
  const buildColumns = useCallback(
    ({ data = [], currTime, users = [], groupBy = "assignedUserId" }) => {
      const getExtendedTime = (e, params = {}) => {
        if (e.attemptStatus !== ATTEMPT_STATUSES.STARTED) return 0;
        const result =
          currTime -
          toHour(
            params.exact
              ? e.displayStartTime
              : toSnappedTime(e.displayStartTime, "floor")
          ) -
          getDurationInHours(e.scheduledStartDate, e.scheduledEndDate);
        return params.exact
          ? result
          : Math.max(0, snapAdjusted(result, SNAP_INTERVAL_IN_HOURS, "floor"));
      };

      const getDelayedTime = (e, time) =>
        e.readOnly
          ? 0
          : snapAdjusted(
              Math.max(0, time - toHour(toSnappedTime(e.displayStartTime))),
              SNAP_INTERVAL_IN_HOURS,
              "floor"
            );

      const getDelayedStartTime = (e) =>
        new Date(
          getDateWithAddedTime(e.displayStartTime, { h: e.delayedTime })
        );

      const getFinalEndTime = (e) =>
        toHour(
          getDateWithAddedTime(toSnappedTime(e.displayEndTime, "floor"), {
            h: e.extendedTime + e.delayedTime,
          })
        );

      return users.reduce((accu, user) => {
        const column = data.filter((event) => event[groupBy] === user.id);
        column.sort((a, b) => {
          const diff =
            new Date(a.displayStartTime) - new Date(b.displayStartTime);

          if (diff === 0) {
            if (a.attemptStatus === ATTEMPT_STATUSES.STARTED) {
              return -1;
            }
            if (b.attemptStatus === ATTEMPT_STATUSES.STARTED) {
              return 1;
            }
            return 0;
          }

          return diff;
        });

        const result = column.map((e) =>
          isToday(e.displayStartTime)
            ? {
                ...e,
                extendedTime: getExtendedTime(e),
                exactExtendedTime: getExtendedTime(e, { exact: true }),
                delayedTime: getDelayedTime(e, currTime),
              }
            : { ...e, exactExtendedTime: 0, extendedTime: 0, delayedTime: 0 }
        );

        result.sort((a, b) => getDelayedStartTime(a) - getDelayedStartTime(b));

        let prevEvent = result[0];
        let latestPrevEndTime = prevEvent && getFinalEndTime(prevEvent);
        for (let i = 1; i < result.length; i++) {
          const currEvent = result[i];
          if (
            isSameDay(prevEvent.displayStartTime, currEvent.displayStartTime)
          ) {
            currEvent.delayedTime = Math.max(
              getDelayedTime(currEvent, latestPrevEndTime),
              currEvent.delayedTime
            );
            latestPrevEndTime = Math.max(
              latestPrevEndTime,
              getFinalEndTime(currEvent)
            );
          } else {
            latestPrevEndTime = getFinalEndTime(currEvent);
          }
          prevEvent = currEvent;
        }

        const hasAnyStartedWork = column.some(
          (e) => e?.attemptStatus === ATTEMPT_STATUSES.STARTED
        );
        return [
          ...accu,
          ...result.map((e) => ({
            ...e,
            ableToStart: !hasAnyStartedWork,
          })),
        ];
      }, []);
    },
    []
  );

  const getDataForDisplay = useCallback(
    ({ data = [], currTime, users = [], groupBy = "assignedUserId" }) => {
      const displayData = buildColumns({ data, currTime, users, groupBy });
      const finalDisplayData = buildUserCapacitiesData({
        displayData,
        groupBy,
        calendarView,
        dateRange,
        date,
        users,
        userCapacities,
        viewConfig,
        selectedTechnician,
      });
      return finalDisplayData;
    },
    [
      userCapacities,
      viewConfig,
      calendarView,
      dateRange,
      date,
      selectedTechnician,
    ]
  );

  // ---------------- Data loaders / services ----------------
  const getAttempts =
    calendarSet?.eventType === ENTITY_TYPES.WORK_ORDER
      ? taskService.attempts.getAllWorkOrders
      : taskService.attempts.getAll;

  const getUserNegotiationOptions = useCallback(
    async (res) => {
      const userGroups = res.map((r) => `users/${r}`);
      const calGroup = `calendars/${calendarSet.id}`;
      const groups = [...userGroups, calGroup].join(",");
      const negotiationOptions = await subscriptionService.negotiation.users({
        data: { groups },
      });
      if (!negotiationOptions) {
        bugsnag.notify("Negotiation options not found");
        return {};
      }
      return negotiationOptions;
    },
    [viewConfig.id, calendarSet.id]
  );

  const getIsRequestedDateInRange = (reqStartDate) => {
    if (!reqStartDate) return true;
    if (calendarView === VIEWS.DAYS) {
      return (
        isSameOrAfter(reqStartDate, dateRange.startDate) &&
        isSameOrBefore(reqStartDate, dateRange.endDate)
      );
    }
    return isSameDay(reqStartDate, date);
  };

  const buildBuckets = useCallback(
    async (viewConfig) => {
      const incomingBuckets = deepCopy(viewConfig.trays) || [];
      const sorted = [...incomingBuckets].sort(
        (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
      );
      const needsWorkOrder = calendarSet.eventType === ENTITY_TYPES.WORK_ORDER;

      const mapped = await Promise.all(
        sorted.map(async (bucket) => {
          const bucketClone = { ...bucket };
          // bucket.filter is an already-formatted OData query string, so we have to create the full OData query string
          //   with the workTemplate filter as well, and pass the full string instead of passing an object to the OData library.
          const filterStringParts = [];
          if (bucket?.filter) {
            filterStringParts.push(`(${bucket?.filter})`);
          }

          if (calendarSet.eventType && viewConfig.orderTemplateIds) {
            filterStringParts.push(
              `assignment/${calendarSet.eventType}/${calendarSet.eventType}TemplateId in (${viewConfig.orderTemplateIds})`
            );
          }

          // TODO : yuck, but elsewhere we are matching on filter to determine type like in the "Tray Type" dropdown in calendar configs
          // this will filter all Follow-Up trays to limit them to the attached Hold Reason Tabs/Calendar Ids, to prevent
          // trays that have no attached calendars (hold reasons) from finding orders from ALL calendars instead of none.
          if (bucket?.filter === FOLLOW_UP_TRAY_FILTERS) {
            if ((bucket?.attachedCalendarIds ?? []).length < 1) {
              // a follow up tray with no attached calendars
              // this bucket will always be empty, so skip the API call
              bucketClone.data = [];
              return bucketClone;
            }
            filterStringParts.push(
              `assignment/${
                calendarSet.eventType
              }/holdCalendarId in (${bucket.attachedCalendarIds.join(",")})`
            );
          }

          const filterString = filterStringParts.join(" and ");

          const { data, status } = await (needsWorkOrder
            ? taskService.attempts.getAllWorkOrders
            : getAttempts)({
            useCache: true,
            filters: filterString,
            sort: bucket?.sort,
          });

          if (status === HTTP_STATUSES.OK) {
            bucketClone.data =
              data?.map((val) => {
                const modifiedVal = attachEventTime(val);
                const reqStartDate =
                  val.attemptNumber === "1"
                    ? val.assignment?.order?.requestedStartDate
                    : null;
                return {
                  ...modifiedVal,
                  isRequestedInDateRange:
                    getIsRequestedDateInRange(reqStartDate),
                  attemptRequestedStartDate: reqStartDate,
                  uuid: createUUID(),
                  expectedDuration: modifiedVal?.expectedDuration,
                };
              }) || [];
          } else {
            bucketClone.data = [];
          }
          return bucketClone;
        })
      );
      return mapped;
    },
    [attachEventTime, getAttempts, dateRange, date, calendarView]
  );

  const loadBuckets = useCallback(
    async (viewConfig = {}) => {
      const newBuckets = await buildBuckets(viewConfig);
      setBuckets((prev) => ({ ...prev, [viewConfig.id]: newBuckets }));
    },
    [buildBuckets]
  );

  const getAvailabilityData = useCallback(async () => {
    let filters;
    if (calendarView === VIEWS.DAYS) {
      filters = {
        userId: { eq: { value: selectedTechnician?.id, type: "guid" } },
        to: { gt: getDate(dateRange.startDate).startOf("day").toDate() },
        from: { lt: getDate(dateRange.endDate).endOf("day").toDate() },
      };
    }
    if (calendarView === VIEWS.DAY) {
      filters = {
        userId: { in: viewConfig.assignableUserIds },
        to: { gt: getDate(date).startOf("day").toDate() },
        from: { lt: getDate(date).endOf("day").toDate() },
      };
    }
    const { data, status } =
      await subscriptionService.users.getUserAvailabilityLogs({
        filters,
      });
    if (status !== HTTP_STATUSES.OK) return [];
    return data;
  }, [dateRange, date, calendarView, viewConfig.assignableUserIds]);

  const getUsers = useCallback(
    async (viewConfig = {}) => {
      const { data: users, status } = await subscriptionService.users.getAll({
        filters: {
          id: { in: viewConfig.assignableUserIds },
        },
        sort: "firstName asc",
      });

      if (status !== HTTP_STATUSES.OK) return [];
      const availabilityLog = await getAvailabilityData();
      users.forEach((user) => {
        user.availabilityLog = availabilityLog.filter(
          (log) => log.userId === user.id
        );
      });
      return users;
    },
    [date, dateRange, calendarView]
  );

  const getUserCapacities = useCallback(
    async (viewConfig = {}) => {
      if (!viewConfig.calendarSetId) return [];
      const { startDate, endDate } =
        calendarView === VIEWS.DAY
          ? { startDate: date, endDate: date }
          : dateRange;

      const { data: capacities, status } =
        await taskService.userCapacities.getForCalendar(
          viewConfig.calendarSetId,
          formatDate(startDate, DATE_TIME_FORMATS.FORMAT_YYYY_MM_DD),
          formatDate(endDate, DATE_TIME_FORMATS.FORMAT_YYYY_MM_DD)
        );

      if (status !== HTTP_STATUSES.OK) return [];

      return capacities;
    },
    [date, dateRange, calendarView, viewConfig]
  );

  // ---------------- Board / users ----------------
  const setSelectedBoard = (board, usersParam) => {
    setSelectedBoardState(board);
    let boardUsersVar = [];
    if (board?.showAllUsers) {
      boardUsersVar = usersParam || users;
    } else {
      boardUsersVar = board?.users
        ?.reduce((acc, user) => {
          const boardUser = (usersParam || users).find(
            ({ id }) => id === user.id
          );
          if (boardUser) {
            acc.push({
              ...boardUser,
              displayOrder: user.displayOrder,
            });
          }
          return acc;
        }, [])
        .sort(sortOnField("displayOrder"));
    }

    setBoardUsers(boardUsersVar);
    appStorage.setItem(SELECTED_BOARD_KEY, board.id);
  };

  const setInitialBoard = useCallback(
    (viewConfig, users) => {
      const localSelectedBoardId = appStorage.getItem(SELECTED_BOARD_KEY);
      const viewBoards = viewConfig.displayBoards || [];
      setBoards(viewBoards);
      if (localSelectedBoardId) {
        const local = viewBoards.find((b) => b.id === localSelectedBoardId);
        if (local) {
          setSelectedBoard(local, users);
          return;
        }
      }
      const def = viewBoards.find((b) => b.default) || viewBoards[0];
      if (def) setSelectedBoard(def, users);
    },
    [setSelectedBoard]
  );

  const loadUsers = useCallback(
    async (viewConfig) => {
      const users = await getUsers(viewConfig);
      if (calendarSet.eventType === ENTITY_TYPES.ORDER) {
        const userCapacities = await getUserCapacities(viewConfig);
        setUserCapacities(userCapacities);
        users.forEach((u) => {
          u.capacities = userCapacities[u.id];
        });
      }
      setInitialBoard(viewConfig, users);
      setUsers(users);
    },
    [getUsers, setInitialBoard, setUsers, viewConfig]
  );

  // ---------------- Carousel controls ----------------
  const clearCarouselTimer = useCallback(() => {
    if (carouselTimerRef.current) {
      clearInterval(carouselTimerRef.current);
      carouselTimerRef.current = null;
    }
  }, []);

  const onChangePrevBoard = useCallback(() => {
    if (!boards.length) return;
    const currentIdx = boards.findIndex((b) => b.id === selectedBoard?.id);
    const prevIdx = (currentIdx - 1 + boards.length) % boards.length;
    setSelectedBoard(boards[prevIdx]);
  }, [boards, selectedBoard, setSelectedBoard]);

  const onChangeNextBoard = useCallback(() => {
    if (!boards.length) return;
    const currentIdx = boards.findIndex((b) => b.id === selectedBoard?.id);
    const nextIdx = (currentIdx + 1) % boards.length;
    setSelectedBoard(boards[nextIdx]);
  }, [boards, selectedBoard, setSelectedBoard]);

  const onChangeNextBoardRef = useRef(onChangeNextBoard);

  useEffect(() => {
    onChangeNextBoardRef.current = onChangeNextBoard;
  }, [onChangeNextBoard]);

  const setCarouselTimer = useCallback(
    (interval = null, mode = carouselMode) => {
      clearCarouselTimer();
      if (mode && interval) {
        carouselTimerRef.current = setInterval(() => {
          onChangeNextBoardRef.current();
        }, interval * 1000);
      }
    },
    [carouselMode, clearCarouselTimer]
  );

  const pauseCarouselMode = useCallback(
    (type) => {
      if (!carouselMode) return;
      pauseCarouselTypesRef.current.add(type);
      const paused = pauseCarouselTypesRef.current.size > 0;
      setIsCarouselPaused(paused);
      if (paused) clearCarouselTimer();
    },
    [carouselMode, clearCarouselTimer]
  );

  const resumeCarouselMode = useCallback(
    (type) => {
      if (!carouselMode) return;
      pauseCarouselTypesRef.current.delete(type);
      const paused = pauseCarouselTypesRef.current.size > 0;
      setIsCarouselPaused(paused);
      if (!paused) setCarouselTimer(carouselInterval);
    },
    [carouselMode, carouselInterval, setCarouselTimer]
  );

  const closeCarouselMode = useCallback(() => {
    setCarouselMode(false);
    clearCarouselTimer();
    setCarouselIntervalState(DEFAULT_CAROUSEL_INTERVAL);
    setIsCarouselPaused(false);
    pauseCarouselTypesRef.current.clear();
  }, [clearCarouselTimer]);

  const openCarouselMode = useCallback(() => {
    setCarouselMode(true);
    setCarouselIntervalState(DEFAULT_CAROUSEL_INTERVAL);
    setCarouselTimer(DEFAULT_CAROUSEL_INTERVAL, true);
  }, [setCarouselTimer]);

  const toggleCarouselMode = useCallback(() => {
    if (carouselMode) closeCarouselMode();
    else openCarouselMode();
  }, [carouselMode, closeCarouselMode, openCarouselMode]);

  // ---------------- Hub / subscriptions ----------------
  const onHubStateChange = useCallback((state) => {
    setIsSignalRConnected(state === HUB_CONNECTED);
  }, []);

  const onUserSignalRUpdate = (events) => {
    setUsers((prev) => {
      if (!Array.isArray(prev)) return prev;
      let next = prev;
      events.forEach(({ user }) => {
        if (!user) return;
        next = next.map((item) =>
          user.id === item.id
            ? {
                ...item,
                id: user.id,
                availabilityLog: user.availabilityLog,
                name: user.name,
                activityStatus: user.activityStatus,
                availabilityStatus: user.availabilityStatus,
                location: user.location,
                modifiedOn: user.modifiedOn,
              }
            : item
        );
      });
      return next;
    });

    setBoardUsers((prev) => {
      if (!Array.isArray(prev)) return prev;
      let next = prev;
      events.forEach(({ user }) => {
        if (!user) return;
        next = next.map((item) =>
          user.id === item.id
            ? {
                ...item,
                id: user.id,
                availabilityLog: user.availabilityLog,
                name: user.name,
                activityStatus: user.activityStatus,
                availabilityStatus: user.availabilityStatus,
                location: user.location,
                modifiedOn: user.modifiedOn,
              }
            : item
        );
      });
      return next;
    });
  };

  const onUserUpdate = useCallback(
    (events) => {
      userSignalEventRef.current = events;
      setUserSignalRUpdated((prev) => prev + 1);
    },
    [date, calendarView, loadUsers, viewConfig, setUserSignalRUpdated]
  );

  useEffect(() => {
    if (calendarView === VIEWS.DAYS || !isSameDay(getDate(), date)) {
      loadUsers(viewConfig);
    } else if (userSignalEventRef.current) {
      onUserSignalRUpdate(userSignalEventRef.current);
    }
  }, [userSignalRUpdated]);

  const updateCloseoutDrawersMapOnAttemptUpdated = (attempt) => {
    setCloseoutDrawersMap((prev) =>
      prev[attempt.id]
        ? {
            ...prev,
            [attempt.id]: { ...prev[attempt.id], attempt },
          }
        : prev
    );
  };

  const addOrRemoveAttemptInBucket = useCallback(
    (attempt, trayIds) => {
      setBuckets((prevBuckets) => {
        const prev = prevBuckets?.[viewConfig.id];
        if (!prev?.length) return prevBuckets;

        const newBuckets = [...prev];
        newBuckets.forEach((b) => {
          // remove from all buckets..
          b.data = b.data.filter((att) => att.id !== attempt.id);
          if (trayIds.includes(b.id)) {
            // then add newer data to only matching buckets
            b.data = [
              {
                ...attempt,
                uuid: createUUID(),
              },
              ...b.data,
            ];
          }
        });
        return { ...prevBuckets, [viewConfig.id]: newBuckets };
      });
    },
    [viewConfig.id]
  );

  const updateAttempt = useCallback(
    (attempt, trayIds = null) => {
      if (!attempt) return;

      if (trayIds) {
        addOrRemoveAttemptInBucket(attempt, trayIds);
      }
      const updatedAttempt = attachEventTime(attempt);
      updateCloseoutDrawersMapOnAttemptUpdated(updatedAttempt);

      const showAttempt = ![
        ATTEMPT_STATUSES.NEW,
        ATTEMPT_STATUSES.CANCELLED,
        ATTEMPT_STATUSES.HOLD,
      ].includes(attempt.attemptStatus);

      setData((prev = []) => {
        const foundIndex = prev.findIndex((item) => item.id === attempt.id);
        if (showAttempt) {
          if (foundIndex === -1) return [...prev, updatedAttempt];
          const foundItem = prev[foundIndex];
          if (attempt.modifiedOn >= foundItem.modifiedOn) {
            const next = prev.toSpliced(foundIndex, 1, {
              ...foundItem,
              ...updatedAttempt,
            });
            return next;
          }
          return prev;
        }
        // do not show attempt
        if (foundIndex !== -1) return prev.toSpliced(foundIndex, 1);
        return prev;
      });
    },
    [
      addOrRemoveAttemptInBucket,
      viewConfig.id,
      attachEventTime,
      updateCloseoutDrawersMapOnAttemptUpdated,
    ]
  );

  const onAttemptUpdate = useCallback(
    (data) => {
      data.forEach(({ attempt, trayIds }) => updateAttempt(attempt, trayIds));
    },
    [viewConfig.id, updateAttempt]
  );

  const subscribe = useCallback(() => {
    const subscriptions = [
      subscribeToHubStateChange(hubRef.current, onHubStateChange),
      subscribeToUserUpdate(hubRef.current, onUserUpdate),
      subscribeToAttemptUpdate(hubRef.current, onAttemptUpdate),
    ];
    return () => {
      subscriptions.forEach((subs) => {
        if (subs && typeof subs.unsubscribe === "function") {
          subs.unsubscribe();
        }
      });
    };
  }, [viewConfig.id, onAttemptUpdate, onHubStateChange, onUserUpdate]);

  const stopAndRemoveHub = useCallback(() => {
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }
    if (hubRef.current) {
      hubRef.current.stop?.();
      removeHubByName(ATTEMPTS_HUB_NAME);
      hubRef.current = null;
    }
  }, []);

  const startConnectionAndSubscribe = useCallback(async () => {
    stopAndRemoveHub();

    hubRef.current = findHubByName(ATTEMPTS_HUB_NAME);

    if (viewConfig?.assignableUserIds?.length || viewConfig.trays.length) {
      if (!hubRef.current) {
        const options = await getUserNegotiationOptions(
          viewConfig?.assignableUserIds
        );
        hubRef.current = createHub(
          ATTEMPTS_HUB_NAME,
          options?.data.url?.replace("signalr-emulator", "localhost"),
          {
            accessTokenFactory: async () => {
              const opts = await getUserNegotiationOptions(
                viewConfig?.assignableUserIds
              );
              return opts?.data?.accessToken;
            },
          },
          true
        );
      }
      hubRef.current.start();
      unsubscribeRef.current = subscribe();
    }
  }, [viewConfig.id, getUserNegotiationOptions, subscribe]);

  // ---------------- Lifecycle cleanup ----------------
  useEffect(
    () => () => {
      // on unmount
      if (carouselTimerRef.current) clearInterval(carouselTimerRef.current);
    },
    [viewConfig.id]
  );

  // ---------------- Public API ----------------
  return {
    // state
    users,
    boards,
    boardUsers,
    selectedBoard,
    data,
    buckets,
    carouselMode,
    carouselInterval,
    isCarouselPaused,
    isSignalRConnected,
    calendarView,
    date,
    dateRange,
    selectedTechnician,
    closeoutDrawersMap,

    // setters / basic actions
    setUsers,
    setBoards,
    setBoardUsers,
    setSelectedBoard,
    setInitialBoard,
    setData,
    setCalendarView,
    setSelectedTechnician,
    setDate,
    setDateRange,
    setBuckets,

    // carousel controls
    clearCarouselTimer,
    onChangePrevBoard,
    onChangeNextBoard,
    setCarouselTimer,
    setCarouselInterval,
    pauseCarouselMode,
    resumeCarouselMode,
    closeCarouselMode,
    openCarouselMode,
    toggleCarouselMode,
    setCarouselMode,

    // data utilities
    attachEventTime,
    checkConflicts,
    getDataForDisplay,

    // loaders
    loadBuckets,
    startConnectionAndSubscribe,
    stopAndRemoveHub,
    loadUsers,

    // updates from hub events
    setCloseoutDrawersMap,
    updateCloseoutDrawersMapOnAttemptUpdated,
    onUserUpdate,
    onAttemptUpdate,
    updateAttempt,

    // services exposed (optional)
    getAttempts,
  };
};
